import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useDirtyGuard } from '@/hooks/useDirtyGuard';
import { useAppSelector } from '@/features/hooks';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { DraftRestoredBanner } from '@/components/common/DraftRestoredBanner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { FieldTooltip } from '@/components/ui/field-tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, DollarSign } from 'lucide-react';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { ColorPicker } from '@/components/ui/color-picker';
import { getCurrencySymbol, getCurrencyStep } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import type { Budget, Category } from '@/lib/types';

// ── Zod schemas ──────────────────────────────────────────────────────────────

const categoryBudgetRowSchema = z.object({
  categoryId: z.string().min(1, 'Select a category'),
  budgeted: z.coerce.number().min(0, 'Must be ≥ 0'),
});

const budgetSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  year: z.coerce.number().int().min(2020).max(2099),
  month: z.coerce.number().int().min(1).max(12),
  budgetMethod: z.enum(['zero-based', 'spending-plan', 'rollover']),
  plannedIncome: z.coerce.number().min(0).default(0),
  categoryBudgets: z.array(categoryBudgetRowSchema).default([]),
  notes: z.string().max(500).optional(),
  emoji: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/i, 'Use a hex color like #3b82f6')
    .default('#3b82f6'),
});

export type BudgetFormData = z.infer<typeof budgetSchema>;

// ── Component ────────────────────────────────────────────────────────────────

interface BudgetFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: BudgetFormData) => Promise<void>;
  budget?: Budget | null;
  /** All user categories — used for the category allocation selector */
  categories?: Category[];
  isLoading?: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function BudgetForm({
  open,
  onOpenChange,
  onSubmit,
  budget,
  categories = [],
  isLoading = false,
}: BudgetFormProps) {
  const isEditing = !!budget;
  const now = new Date();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const { currency } = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);
  const step = getCurrencyStep(currency);

  // Only show expense categories for spending allocation
  const expenseCategories = React.useMemo(
    () => categories.filter((c) => c.type === 'expense'),
    [categories]
  );

  const form = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    defaultValues: {
      name: '',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      budgetMethod: 'zero-based',
      plannedIncome: 0,
      categoryBudgets: [],
      notes: '',
      emoji: '',
      color: '#3b82f6',
    },
  });

  // Dynamic category allocation rows
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'categoryBudgets',
  });

  // Draft auto-save (create mode only)
  const { clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore } = useFormDraft<BudgetFormData>(
    isEditing ? 'budget-edit' : 'budget-create',
    userId,
    form,
    !isEditing && open
  );

  const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard, saveDraftAndClose } =
    useDirtyGuard(form, onOpenChange, clearDraft, saveDraftNow);

  // Populate form when editing or reset when creating
  useEffect(() => {
    if (budget) {
      form.reset({
        name: budget.name,
        year: budget.year,
        month: budget.month,
        budgetMethod: budget.budgetMethod,
        plannedIncome: budget.income?.planned ?? 0,
        categoryBudgets: (budget.categories ?? []).map((c) => ({
          categoryId: c.categoryId,
          budgeted: c.budgeted,
        })),
        notes: budget.notes || '',
        emoji: budget.emoji || '',
        color: budget.color || '#3b82f6',
      });
    } else {
      form.reset({
        name: `${MONTHS[now.getMonth()]} ${now.getFullYear()} Budget`,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        budgetMethod: 'zero-based',
        plannedIncome: 0,
        categoryBudgets: [],
        notes: '',
        emoji: '',
        color: '#3b82f6',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget, open]);

  const handleSubmit = async (data: BudgetFormData) => {
    await onSubmit(data);
    clearDraft();
    form.reset();
  };

  // Derived allocation totals
  const watchedCategoryBudgets = form.watch('categoryBudgets');
  const watchedIncome = Number(form.watch('plannedIncome')) || 0;
  const totalAllocated = watchedCategoryBudgets.reduce(
    (sum, row) => sum + (Number(row.budgeted) || 0),
    0
  );
  const unallocated = watchedIncome - totalAllocated;

  // IDs already in the list — disable them in other rows to prevent duplicates
  const addedCategoryIds = new Set(fields.map((f) => f.categoryId).filter(Boolean));

  return (
    <>
      <Sheet open={open} onOpenChange={guardedOnOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isEditing ? 'Edit Budget' : 'Create Budget'}</SheetTitle>
            <SheetDescription>
              {isEditing
                ? 'Update your budget details and category spending limits.'
                : 'Set up a monthly budget and assign spending limits to categories.'}
            </SheetDescription>
          </SheetHeader>

          {justRestored && (
            <DraftRestoredBanner
              age={restoredAge}
              onDismiss={dismissRestore}
              className="mx-0 mt-4"
            />
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 mt-4 pb-4">
              {/* ── Budget Name ──────────────────────────────────────────── */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Budget Name
                      <RequiredAsterisk />
                      <FieldTooltip text="Give this budget a unique name. E.g., 'Monthly Essentials', 'Holiday Spending'." />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., March 2026 Budget" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ── Emoji + Color ─────────────────────────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="emoji"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emoji<OptionalTag /></FormLabel>
                      <FormControl>
                        <EmojiPicker value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Color<OptionalTag /></FormLabel>
                      <FormControl>
                        <ColorPicker value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* ── Year & Month ──────────────────────────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year<RequiredAsterisk /></FormLabel>
                      <FormControl>
                        <Input type="number" min={2020} max={2099} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Month<RequiredAsterisk /></FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(parseInt(v))}
                        value={String(field.value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MONTHS.map((name, i) => (
                            <SelectItem key={i} value={String(i + 1)}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* ── Budget Method ──────────────────────────────────────────── */}
              <FormField
                control={form.control}
                name="budgetMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Budget Method
                      <OptionalTag />
                      <FieldTooltip text="Zero-Based: allocate every dollar of income. Spending Plan: set limits per category. Rollover: unused amounts carry forward to the next month." />
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="zero-based">Zero-Based</SelectItem>
                        <SelectItem value="spending-plan">Spending Plan</SelectItem>
                        <SelectItem value="rollover">Rollover</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ── Planned Income ────────────────────────────────────────── */}
              <FormField
                control={form.control}
                name="plannedIncome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Planned Income
                      <OptionalTag />
                      <FieldTooltip text="Your expected monthly income. Used to track how much remains unallocated after category spending limits are set." />
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">
                          {currencySymbol}
                        </span>
                        <Input
                          type="number"
                          min={0}
                          step={step}
                          className="pl-8"
                          placeholder="0.00"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              {/* ── Category Allocations ──────────────────────────────────── */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      Category Allocations
                      <OptionalTag />
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Set spending limits per category. Actual spending will be tracked automatically.
                    </p>
                  </div>
                  {fields.length > 0 && watchedIncome > 0 && (
                    <Badge
                      variant={unallocated < 0 ? 'destructive' : unallocated === 0 ? 'secondary' : 'outline'}
                      className="text-xs shrink-0 ml-2 whitespace-nowrap"
                    >
                      {unallocated < 0
                        ? `${currencySymbol}${Math.abs(unallocated).toFixed(2)} over`
                        : `${currencySymbol}${unallocated.toFixed(2)} left`}
                    </Badge>
                  )}
                </div>

                {/* Visual allocation bar */}
                {watchedIncome > 0 && fields.length > 0 && (
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        totalAllocated > watchedIncome ? 'bg-destructive' : 'bg-primary'
                      }`}
                      style={{
                        width: `${Math.min((totalAllocated / watchedIncome) * 100, 100)}%`,
                      }}
                    />
                  </div>
                )}

                {/* Category row list */}
                <div className="space-y-2">
                  {fields.map((field, index) => {
                    const selectedCat = expenseCategories.find(
                      (c) => c.id === fields[index].categoryId
                    );
                    return (
                      <div key={field.id} className="flex items-start gap-2">
                        {/* Category selector */}
                        <FormField
                          control={form.control}
                          name={`categoryBudgets.${index}.categoryId`}
                          render={({ field: f }) => (
                            <FormItem className="flex-1 min-w-0">
                              <Select onValueChange={f.onChange} value={f.value}>
                                <FormControl>
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select category">
                                      {selectedCat && (
                                        <span className="flex items-center gap-1.5 truncate">
                                          {selectedCat.emoji && <span>{selectedCat.emoji}</span>}
                                          <span
                                            className="w-2.5 h-2.5 rounded-full shrink-0"
                                            style={{ backgroundColor: selectedCat.color || '#6b7280' }}
                                          />
                                          <span className="truncate">{selectedCat.name}</span>
                                        </span>
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {expenseCategories.map((c) => (
                                    <SelectItem
                                      key={c.id}
                                      value={c.id}
                                      disabled={addedCategoryIds.has(c.id) && c.id !== f.value}
                                    >
                                      <span className="flex items-center gap-2">
                                        {c.emoji && <span>{c.emoji}</span>}
                                        <span
                                          className="w-2.5 h-2.5 rounded-full shrink-0"
                                          style={{ backgroundColor: c.color || '#6b7280' }}
                                        />
                                        <span>{c.name}</span>
                                      </span>
                                    </SelectItem>
                                  ))}
                                  {expenseCategories.length === 0 && (
                                    <SelectItem value="__none__" disabled>
                                      No categories found
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Budgeted amount */}
                        <FormField
                          control={form.control}
                          name={`categoryBudgets.${index}.budgeted`}
                          render={({ field: f }) => (
                            <FormItem className="w-28 shrink-0">
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs select-none">
                                    {currencySymbol}
                                  </span>
                                  <Input
                                    type="number"
                                    min={0}
                                    step={step}
                                    className="pl-5 h-9 text-sm"
                                    placeholder="0"
                                    {...f}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Remove row */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => remove(index)}
                          aria-label="Remove category"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Add allocation button */}
                {expenseCategories.length > fields.length && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 text-muted-foreground border-dashed"
                    onClick={() => append({ categoryId: '', budgeted: 0 })}
                  >
                    <Plus className="h-4 w-4" />
                    Add Category Limit
                  </Button>
                )}

                {/* Total summary row */}
                {fields.length > 0 && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Total allocated: {currencySymbol}{totalAllocated.toFixed(2)}
                    </span>
                    {watchedIncome > 0 && (
                      <span>of {currencySymbol}{watchedIncome.toFixed(2)} income</span>
                    )}
                  </div>
                )}
              </div>

              <Separator />

              {/* ── Notes ────────────────────────────────────────────────── */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Notes
                      <OptionalTag />
                      <FieldTooltip text="Internal notes about this budget — goals, reminders, or context." />
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any notes about this budget..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <SheetFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guardedOnOpenChange(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditing ? 'Update Budget' : 'Create Budget'}
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <DiscardChangesDialog
        open={showDiscard}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        onSaveDraft={!isEditing ? saveDraftAndClose : undefined}
      />
    </>
  );
}
